import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { PLATFORMS, useRentals, RentalListing } from "@/hooks/use-rentals";
import { useWorkspace } from "@/hooks/use-workspace";
import { Globe, CheckCircle2, Clock, XCircle, ExternalLink, Info } from "lucide-react";

// ── Platform logos & colors ────────────────────────────────────────────────
const PLATFORM_META: Record<string, { bg: string; initial: string; feedBased: boolean }> = {
  zillow:         { bg: "bg-[#006aff]", initial: "Z", feedBased: true  },
  trulia:         { bg: "bg-[#00b04f]", initial: "T", feedBased: true  },
  hotpads:        { bg: "bg-[#ff6b2b]", initial: "H", feedBased: true  },
  apartments_com: { bg: "bg-[#00838f]", initial: "A", feedBased: true  },
  rent_com:       { bg: "bg-[#f5a623]", initial: "R", feedBased: true  },
  realtor_com:    { bg: "bg-[#d92228]", initial: "R", feedBased: false },
  facebook:       { bg: "bg-[#1877f2]", initial: "F", feedBased: false },
  craigslist:     { bg: "bg-[#5d2da9]", initial: "C", feedBased: false },
};

const syndicationStatusStyle: Record<
  string,
  { icon: React.ReactNode; label: string; cls: string }
> = {
  published: {
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
    label: "Live",
    cls: "bg-green-500/15 text-green-400 border-green-500/28",
  },
  pending: {
    icon: <Clock className="w-3.5 h-3.5" />,
    label: "Pending",
    cls: "bg-amber-400/15 text-amber-400 border-amber-400/28",
  },
  failed: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: "Failed",
    cls: "bg-destructive/15 text-destructive border-destructive/28",
  },
  removed: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: "Removed",
    cls: "bg-muted text-muted-foreground border-border",
  },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listing: RentalListing | null;
}

export function PublishDialog({ open, onOpenChange, listing }: Props) {
  const { publishToPlatforms, getSyndicationsForListing } = useRentals();
  const { currentWorkspace } = useWorkspace();
  const [selected, setSelected] = useState<string[]>([]);

  if (!listing) return null;

  const existingSyndications = getSyndicationsForListing(listing.id);
  const synMap = new Map(existingSyndications.map((s) => [s.platform, s]));

  const toggle = (platformId: string) =>
    setSelected((prev) =>
      prev.includes(platformId) ? prev.filter((p) => p !== platformId) : [...prev, platformId],
    );

  const handlePublish = () => {
    if (!selected.length) return;
    publishToPlatforms.mutate(
      { listingId: listing.id, platforms: selected },
      {
        onSuccess: () => {
          onOpenChange(false);
          setSelected([]);
        },
      },
    );
  };

  const feedUrl = currentWorkspace
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rental-feed?workspace_id=${currentWorkspace.id}`
    : "";

  const costarFeedUrl = currentWorkspace
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/costar-feed?workspace_id=${currentWorkspace.id}`
    : "";

  const groups = Array.from(new Set(PLATFORMS.map((p) => p.group)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-[17px]">
            <Globe className="h-5 w-5 text-primary" />
            Publish "{listing.title}"
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Select platforms to syndicate this listing. Feed-based platforms sync via XML — Zillow Group
            and CoStar Group each use a separate feed URL shown below.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[60vh]">
          {/* Zillow feed info banner */}
          <div className="mx-5 mt-4 flex items-start gap-2.5 px-3.5 py-3 rounded-[10px] bg-muted border border-border text-[12.5px] text-muted-foreground">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-foreground mb-0.5">Zillow / Trulia / HotPads feed</p>
              <p>
                Zillow crawls your XML feed and applies changes within minutes (real-time sync is triggered
                automatically). Submit the URL below to{" "}
                <span className="text-primary font-medium">rentalfeeds@zillow.com</span> to activate the feed.
              </p>
              {feedUrl && (
                <code className="mt-2 block bg-background border border-border rounded-md px-2.5 py-1.5 text-[11px] font-mono text-foreground truncate">
                  {feedUrl}
                </code>
              )}
            </div>
          </div>

          {/* CoStar feed info banner */}
          <div className="mx-5 mt-2.5 flex items-start gap-2.5 px-3.5 py-3 rounded-[10px] bg-muted border border-border text-[12.5px] text-muted-foreground">
            <Info className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-foreground mb-0.5">Apartments.com / Rent.com feed</p>
              <p>
                CoStar Group uses a separate XML feed. Submit the URL below in the{" "}
                <span className="text-primary font-medium">CoStar partner portal</span> to activate.
                Requires a CoStar partner agreement and{" "}
                <span className="font-medium text-foreground">COSTAR_PARTNER_ID</span> env secret.
              </p>
              {costarFeedUrl && (
                <code className="mt-2 block bg-background border border-border rounded-md px-2.5 py-1.5 text-[11px] font-mono text-foreground truncate">
                  {costarFeedUrl}
                </code>
              )}
            </div>
          </div>

          {/* Platform list */}
          <div className="px-5 py-4 space-y-5">
            {groups.map((group) => (
              <div key={group}>
                <p className="text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground mb-2.5">
                  {group}
                </p>
                <div className="space-y-2">
                  {PLATFORMS.filter((p) => p.group === group).map((platform) => {
                    const meta   = PLATFORM_META[platform.id];
                    const syn    = synMap.get(platform.id);
                    const synUi  = syn ? syndicationStatusStyle[syn.status] : null;
                    const isLive = syn?.status === "published";
                    const isSelected = selected.includes(platform.id);

                    return (
                      <button
                        key={platform.id}
                        type="button"
                        disabled={isLive}
                        onClick={() => toggle(platform.id)}
                        className={cn(
                          "w-full grid grid-cols-[36px_1fr_auto] gap-3 items-center p-3 rounded-[10px] border transition-colors text-left",
                          isLive
                            ? "border-border bg-muted/50 opacity-70 cursor-default"
                            : isSelected
                              ? "border-destructive bg-destructive/6"
                              : "border-border bg-muted hover:border-border/70",
                        )}
                      >
                        {/* Logo pip */}
                        <div
                          className={cn(
                            "w-9 h-9 rounded-lg grid place-items-center text-sm font-black text-white flex-shrink-0",
                            meta?.bg ?? "bg-muted-foreground",
                          )}
                        >
                          {meta?.initial ?? platform.name[0]}
                        </div>

                        {/* Name + tags */}
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-semibold text-foreground flex items-center gap-2">
                            {platform.name}
                            {meta?.feedBased && (
                              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-400 bg-blue-400/12 border border-blue-400/25 px-1.5 py-px rounded-full">
                                XML Feed
                              </span>
                            )}
                          </div>
                          <div className="text-[11.5px] text-muted-foreground mt-px">{platform.group}</div>
                        </div>

                        {/* Status or selection indicator */}
                        <div className="flex-shrink-0">
                          {synUi ? (
                            <span
                              className={cn(
                                "inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full border",
                                synUi.cls,
                              )}
                            >
                              {synUi.icon}{synUi.label}
                            </span>
                          ) : (
                            <div
                              className={cn(
                                "w-[22px] h-[22px] rounded-full border-[1.5px] grid place-items-center",
                                isSelected ? "border-destructive" : "border-border",
                              )}
                            >
                              {isSelected && (
                                <span className="w-2.5 h-2.5 rounded-full bg-destructive" />
                              )}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="px-5 py-4 border-t border-border gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handlePublish}
            disabled={!selected.length || publishToPlatforms.isPending}
            className="gap-1.5"
          >
            {publishToPlatforms.isPending
              ? "Syncing…"
              : `Publish to ${selected.length} platform${selected.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
